import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Plus, ClipboardList } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import JobCard from "@/components/JobCard";
import { Loader, EmptyState } from "@/components/common";
import { useApi, errMsg } from "@/lib/api";
import { NearFilter } from "@/pages/People";
import { useMe } from "@/context/MeContext";
import { SPECS } from "@/lib/labels";

export default function Jobs() {
  const api = useApi();
  const { me } = useMe();
  const [q, setQ] = useState("");
  const [spec, setSpec] = useState("all");
  const [site, setSite] = useState("all");
  const [near, setNear] = useState("");
  const [radius, setRadius] = useState(250);
  const [jobs, setJobs] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (spec !== "all") params.set("specialization", spec);
    if (site !== "all") params.set("site_type", site);
    if (near.trim()) { params.set("near", near.trim()); params.set("radius_km", radius); }
    const t = setTimeout(() => api("get", `/jobs?${params}`).then((d) => { setJobs(d); setError(""); })
      .catch((e) => { setJobs([]); setError(errMsg(e)); }), near.trim() ? 900 : 300);
    return () => clearTimeout(t);
  }, [q, spec, site, near, radius, api]);

  const isCompany = me.role === "company";
  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Job board</p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight">Job offers</h1>
        </div>
        <div className="flex gap-2">
          {isCompany ? (
            <>
              <Button asChild variant="outline" data-testid="my-jobs-link"><Link to="/my-jobs"><ClipboardList size={16} />My jobs</Link></Button>
              <Button asChild data-testid="company-create-job-button"><Link to="/jobs/new"><Plus size={16} />Post a job</Link></Button>
            </>
          ) : (
            <Button asChild variant="outline" data-testid="my-applications-link"><Link to="/applications"><ClipboardList size={16} />My applications</Link></Button>
          )}
        </div>
      </div>
      <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="relative md:col-span-12 xl:col-span-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="job-filter-search" className="pl-9 h-11 bg-white" placeholder="Search: position, location, company..." value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="md:col-span-6 xl:col-span-4 md:[&>div]:w-full"><NearFilter near={near} setNear={setNear} radius={radius} setRadius={setRadius} prefix="jobs" /></div>
        <Select value={spec} onValueChange={setSpec}>
          <SelectTrigger data-testid="job-filter-specialization" className="md:col-span-3 xl:col-span-2 h-11 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All specializations</SelectItem>
            {Object.entries(SPECS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={site} onValueChange={setSite}>
          <SelectTrigger data-testid="job-filter-location" className="md:col-span-3 xl:col-span-2 h-11 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Onshore & offshore</SelectItem>
            <SelectItem value="onshore">Onshore</SelectItem>
            <SelectItem value="offshore">Offshore</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="mt-6">
        {jobs === null ? <Loader /> : jobs.length === 0 ? (
          <EmptyState testId="jobs-empty" title={error || "No jobs found"} text="Change the filters or check back later." />
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="jobs-list">
            {jobs.map((j, i) => <JobCard key={j.id} job={j} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
